import React, { useState, useEffect } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import { getCsrfToken } from '../lib/csrf.js';

const COURSE_PORTAL_URL =
  'https://123ceu.com/signin/openid?provider=177522925469917&redirectUrl=https%3A%2F%2F123ceu.com%2F&action=login';

function buildStudentNav(user, { onTrainingClick }) {
  const items = [
    { label: 'Dashboard', href: '/', kind: 'inertia', icon: DashboardIcon },
    { label: 'My Training', href: COURSE_PORTAL_URL, kind: 'external', onClick: onTrainingClick, icon: BookIcon },
  ];

  if (user?.student_type === 'Administrator') {
    items.push({ label: 'Live Webinars', href: '/webinars', kind: 'inertia', icon: CalendarIcon });
  }

  items.push({ label: 'Certificates', href: '/certificates', kind: 'inertia', icon: DocumentIcon });
  items.push({ label: 'Profile', href: '/profile', kind: 'inertia', icon: UserIcon });

  if (user?.self_purchased) {
    items.push({ label: 'Billing', href: '/student_purchase/billing_portal', kind: 'inertia', icon: CreditCardIcon });
  }

  items.push({ label: 'Support', href: '/support', kind: 'inertia', icon: SupportIcon });

  return items;
}

// Returns sections, not a flat list. Each section gets a small uppercase
// heading in the sidebar so the 10+ items don't read as one undifferentiated
// scroll. `label: null` would render headingless if needed.
function buildSuperadminNav() {
  return [
    {
      label: null,
      items: [
        { label: 'Dashboard', href: '/superadmin/dashboard', kind: 'inertia', icon: DashboardIcon },
      ],
    },
    {
      label: 'People',
      items: [
        { label: 'Users', href: '/superadmin/users', kind: 'inertia', icon: UsersIcon },
        { label: 'Reviewers', href: '/superadmin/reviewers', kind: 'inertia', icon: ClipboardCheckIcon },
        { label: 'Instructors', href: '/superadmin/instructors', kind: 'inertia', icon: MicrophoneIcon },
        { label: 'Sales Reps', href: '/superadmin/sales_reps', kind: 'inertia', icon: CreditCardIcon },
      ],
    },
    {
      label: 'Content',
      items: [
        { label: 'Announcements', href: '/superadmin/announcements', kind: 'inertia', icon: MegaphoneIcon },
        { label: 'LW Courses', href: '/superadmin/admin_courses', kind: 'inertia', icon: BookIcon },
        { label: 'Courses', href: '/superadmin/courses', kind: 'inertia', icon: BookIcon },
        { label: 'Course Reviews', href: '/superadmin/course_reviews', kind: 'inertia', icon: DocumentIcon },
        { label: 'Sources', href: '/superadmin/sources', kind: 'inertia', icon: TagIcon },
        { label: 'NAB Domains', href: '/superadmin/nab_domains', kind: 'inertia', icon: GlobeIcon },
        { label: 'License Mappings', href: '/superadmin/license_mappings', kind: 'inertia', icon: TagIcon },
        { label: 'Email Journeys', href: '/superadmin/email_journeys', kind: 'inertia', icon: InboxIcon },
      ],
    },
    {
      label: 'Sales',
      items: [
        { label: 'Deals', href: '/superadmin/deals', kind: 'inertia', icon: CreditCardIcon },
      ],
    },
    {
      label: 'Live Webinars',
      items: [
        { label: 'Webinars', href: '/superadmin/webinars', kind: 'inertia', icon: CalendarIcon },
      ],
    },
    {
      label: 'Support',
      items: [
        { label: 'Support Inbox', href: '/superadmin/support_tickets', kind: 'inertia', icon: InboxIcon },
      ],
    },
  ];
}

// A facility owner can also be a learner — someone who bought a group deal
// covering their own recertification, a student moved onto a group account
// partway through it, or an individual who converted their own account into a
// facility account. A student_type is what marks them as a learner, so their
// own training links follow it rather than user_type; without them the
// management chrome would hide coursework they still have to complete.
// Live Webinars stays Administrator-only, matching the server entitlement in
// WebinarsController#require_administrator_student.
function buildGroupManagerNav(activeFacility, user, { onTrainingClick } = {}) {
  const facilityId = activeFacility?.id;
  const isLearner = Boolean(user?.student_type);

  const ownTraining = isLearner ? [
    { label: 'My Training', href: COURSE_PORTAL_URL, kind: 'external', onClick: onTrainingClick, icon: BookIcon },
    ...(user?.student_type === 'Administrator'
      ? [{ label: 'Live Webinars', href: '/webinars', kind: 'inertia', icon: CalendarIcon }]
      : []),
    { label: 'Certificates', href: '/certificates', kind: 'inertia', icon: DocumentIcon },
  ] : [];

  return [
    { label: 'Dashboard', href: '/', kind: 'inertia', icon: DashboardIcon },
    ...ownTraining,
    { label: 'Team Members', href: '/team', kind: 'inertia', icon: UsersIcon },
    { label: 'Assign Training', href: '/assign-training', kind: 'inertia', icon: ClipboardCheckIcon },
    { label: 'Progress Tracking', href: '/progress', kind: 'inertia', icon: ChartBarIcon },
    { label: 'Documents', href: '/documents', kind: 'inertia', icon: DocumentIcon },
    { label: 'Billing', href: facilityId ? `/facilities/${facilityId}/billing` : '/billing', kind: 'inertia', icon: CreditCardIcon },
    ...(isLearner && user?.self_purchased
      ? [{ label: 'My Billing', href: '/student_purchase/billing_portal', kind: 'inertia', icon: CreditCardIcon }]
      : []),
    { label: 'Facility Settings', href: facilityId ? `/facilities/${facilityId}/edit` : '/settings', kind: 'inertia', icon: CogIcon },
    { label: 'Support', href: '/support', kind: 'inertia', icon: SupportIcon },
  ];
}

function FacilitySwitcher({ activeFacility, ownedFacilities }) {
  if (!activeFacility) return null;
  const multiple = ownedFacilities.length >= 2;

  const handleChange = (e) => {
    const id = e.target.value;
    if (!id || Number(id) === activeFacility.id) return;
    const pathname = window.location.pathname;
    const facilityScoped = pathname.match(/^\/facilities\/\d+(\/.*)?$/);
    const redirectTo = facilityScoped ? `/facilities/${id}${facilityScoped[1] || ''}` : pathname;
    router.post('/facilities/set_active', { facility_id: id, redirect_to: redirectTo }, { preserveScroll: true });
  };

  return (
    <div className="-mx-2 mb-2">
      <label className="block px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Facility
      </label>
      <div className="mt-1 flex items-stretch gap-1.5">
        {multiple ? (
          <div className="flex-1 min-w-0">
            <select
              value={activeFacility.id}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-2 text-sm font-medium text-black focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
            >
              {ownedFacilities.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex-1 min-w-0 truncate rounded-md border border-gray-300 bg-gray-50 px-2 py-2 text-sm font-medium text-black">
            {activeFacility.name}
          </div>
        )}
        <Link
          href="/facilities/new"
          title="Add a new facility"
          aria-label="Add a new facility"
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2 text-gray-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function NavItem({ item, onNavigate }) {
  const className =
    'group -mx-2 flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-black hover:bg-indigo-600 hover:text-white';

  const content = (
    <>
      {item.icon}
      {item.label}
    </>
  );

  if (item.kind === 'inertia') {
    return (
      <Link href={item.href} onClick={onNavigate} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={item.href}
      onClick={(e) => {
        item.onClick?.(e);
        if (!e.defaultPrevented) onNavigate?.();
      }}
      className={className}
    >
      {content}
    </a>
  );
}

const navIconClass = 'h-6 w-6 shrink-0';

const DashboardIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 8.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 018.25 20.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);

const BookIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const CalendarIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const DocumentIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const UserIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const CreditCardIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
  </svg>
);

const SupportIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
  </svg>
);

const UsersIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const GlobeIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

const ClipboardCheckIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
  </svg>
);

const TagIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
  </svg>
);

const MegaphoneIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
  </svg>
);

const InboxIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
  </svg>
);

const ChartBarIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const CogIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const VideoCameraIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const MicrophoneIcon = (
  <svg className={navIconClass} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
  </svg>
);

const LockIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c-1.104 0-2 .896-2 2v2h4v-2c0-1.104-.896-2-2-2zm6 4v-4a6 6 0 10-12 0v4a2 2 0 00-2 2v4a2 2 0 002 2h12a2 2 0 002-2v-4a2 2 0 00-2-2zm-10-4a4 4 0 118 0v4H8v-4z" />
  </svg>
);

const ExitIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

export default function Layout({ children, flash }) {
  const { auth, flash: sharedFlash } = usePage().props;
  const user = auth?.user;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Fall back to the Inertia-shared flash (set by ApplicationController) so
  // pages don't have to thread it through manually — a redirect_to with alert:
  // from any controller will surface here automatically.
  const [localFlash, setLocalFlash] = useState(flash ?? sharedFlash ?? {});
  const [navigating, setNavigating] = useState(false);
  const [navigatingMessage, setNavigatingMessage] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // Local state is a copy so a banner can be dismissed, but it MUST re-sync
  // when Inertia swaps the page props. Every superadmin CRUD screen redirects
  // on both success and failure, so without this the layout keeps rendering the
  // flash captured at mount and the outcome of the redirect is never shown.
  // Rails sweeps a served flash, so a later navigation resets this to empty and
  // a consumed message cannot reappear.
  useEffect(() => {
    setLocalFlash(flash ?? sharedFlash ?? {});
  }, [flash, sharedFlash]);

  useEffect(() => {
    const startHandler = () => setNavigating(true);
    const endHandler = () => {
      setNavigating(false);
      setNavigatingMessage(null);
    };
    const offStart = router.on('start', startHandler);
    const offFinish = router.on('finish', endHandler);
    return () => {
      offStart();
      offFinish();
    };
  }, []);

  // When the user navigates back via bfcache after an external redirect,
  // the page is restored from cache with our navigating state still true.
  // Clear it so the spinner doesn't stick.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setNavigating(false);
        setNavigatingMessage(null);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Only navigate once the server has actually dropped the impersonation. If
  // the POST fails and we navigate anyway, /superadmin/users still sees the
  // impersonated (non-superadmin) identity and bounces to the portal root —
  // which reads exactly like "I silently lost my superadmin role".
  const handleStopImpersonating = async () => {
    const csrfToken = getCsrfToken();
    try {
      const response = await fetch('/stop_impersonating', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
      });
      if (!response.ok) {
        setLocalFlash({ alert: "Couldn't stop impersonating. Please reload the page and try again." });
        return;
      }
    } catch {
      setLocalFlash({ alert: "Couldn't stop impersonating — network error. Please try again." });
      return;
    }
    window.location.href = '/superadmin/users';
  };

  const handleCoursePortalClick = (e) => {
    if (!user?.email_verified) {
      e.preventDefault();
      setShowVerifyModal(true);
      return;
    }
    // Open-in-new-tab clicks (cmd/ctrl/middle/shift) keep the current page
    // visible, so the spinner isn't helpful there — skip it.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    // External SSO redirect chain takes a couple seconds. Show the overlay
    // so the user knows their click registered. The browser navigation
    // unmounts this component, so we don't need to clear the state.
    setMobileMenuOpen(false);
    setNavigatingMessage('Opening your training portal…');
    setNavigating(true);
  };

  const handleResendVerification = async () => {
    const csrfToken = getCsrfToken();
    await fetch('/api/email_verifications/resend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ email: user?.email }),
    });
    setShowVerifyModal(false);
    setLocalFlash({ notice: 'Verification email sent. Check your inbox.' });
  };

  const handleLogout = () => {
    router.delete('/logout');
  };

  const page = usePage();
  const { active_facility: activeFacility, owned_facilities: ownedFacilities = [] } = page.props;
  const onSuperadminPath = (page.url || '').startsWith('/superadmin');
  const isSuperadminNav = onSuperadminPath && user?.superadmin;
  const navItems = isSuperadminNav
    ? buildSuperadminNav()
    : user?.user_type === 'student'
      ? buildStudentNav(user, { onTrainingClick: handleCoursePortalClick })
      : buildGroupManagerNav(activeFacility, user, { onTrainingClick: handleCoursePortalClick });

  const showSuperadminLink = !isSuperadminNav && user?.superadmin;

  const renderNav = (onNavigate) => (
    <div className="flex flex-1 flex-col">
      {!isSuperadminNav && user?.user_type === 'group_manager' && (
        <FacilitySwitcher activeFacility={activeFacility} ownedFacilities={ownedFacilities} />
      )}
      <ul className="flex flex-1 flex-col gap-y-1">
        {isSuperadminNav
          ? navItems.map((section, idx) => (
              <li key={section.label || `section-${idx}`} className={idx > 0 ? 'mt-3' : ''}>
                {section.label && (
                  <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    {section.label}
                  </div>
                )}
                <ul className="flex flex-col gap-y-1">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <NavItem item={item} onNavigate={onNavigate} />
                    </li>
                  ))}
                </ul>
              </li>
            ))
          : navItems.map((item) => (
              <li key={item.label}>
                <NavItem item={item} onNavigate={onNavigate} />
              </li>
            ))}
        {isSuperadminNav && (
          <li className="mt-auto">
            <NavItem
              item={{ label: 'Exit Superadmin', href: '/', kind: 'inertia', icon: ExitIcon }}
              onNavigate={onNavigate}
            />
          </li>
        )}
        {showSuperadminLink && (
          <li className="mt-auto">
            <NavItem
              item={{ label: 'Superadmin', href: '/superadmin/dashboard', kind: 'inertia', icon: LockIcon }}
              onNavigate={onNavigate}
            />
          </li>
        )}
        <li className={isSuperadminNav || showSuperadminLink ? '' : 'mt-auto'}>
          <button
            onClick={handleLogout}
            className="cursor-pointer group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-black hover:bg-indigo-600 hover:text-white"
          >
            Sign out
          </button>
        </li>
      </ul>
    </div>
  );

  return (
    <div>
      {/* Loading overlay */}
      {navigating && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-white/60"
          role="status"
          aria-live="polite"
        >
          <svg className="h-8 w-8 animate-spin text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {navigatingMessage && (
            <p className="text-sm font-medium text-gray-700">{navigatingMessage}</p>
          )}
        </div>
      )}

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu */}
      <div className={`fixed inset-y-0 left-0 z-40 w-72 transform overflow-y-auto bg-white pb-6 transition duration-300 ease-in-out lg:hidden ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="mt-5 flex h-16 items-center justify-between px-6">
          <Link href="/">
            <img src="/logo.png" alt="123CEU" className="h-10 w-auto" />
          </Link>
          <button type="button" onClick={() => setMobileMenuOpen(false)} className="text-black hover:text-black">
            <span className="sr-only">Close menu</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="mt-6 px-6">{renderNav(() => setMobileMenuOpen(false))}</nav>
      </div>

      {/* Mobile header */}
      <div data-testid="mobile-header" className="sticky top-0 z-30 flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
        <button type="button" onClick={() => setMobileMenuOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
          <span className="sr-only">Open sidebar</span>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <div className="flex items-center gap-x-4 lg:gap-x-6">
            <img src="/logo.png" alt="123CEU" className="h-8 w-auto" />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r border-gray-200">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
          <div className="flex mt-5">
            <Link href="/">
              <img src="/logo.png" alt="123CEU" className="h-10 w-120px m-auto" />
            </Link>
          </div>
          <nav className="flex flex-1 flex-col">{renderNav()}</nav>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-600/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900">Verify your email</h3>
            <p className="mt-2 text-sm text-gray-600">
              You need to verify your email address before you can access your training. Check your inbox for a verification link.
            </p>
            <div className="mt-5 flex gap-3 justify-end">
              <button onClick={() => setShowVerifyModal(false)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                Close
              </button>
              <button onClick={handleResendVerification}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer">
                Resend verification email
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="lg:pl-72">
        {user?.impersonating && (
          <div className="bg-yellow-400 text-black text-center py-2 px-4 font-bold z-50">
            You are impersonating user #{user.impersonated_user_id}
            <button onClick={handleStopImpersonating} className="underline ml-2 cursor-pointer">
              Stop impersonating
            </button>
          </div>
        )}
        {localFlash?.alert && (
          <div className="mb-4 bg-red-500 px-4 py-3 text-white text-center font-semibold shadow-sm cursor-pointer" onClick={() => setLocalFlash(f => ({ ...f, alert: null }))}>
            {localFlash.alert}
          </div>
        )}
        {localFlash?.notice && (
          <div className="mb-4 bg-green-500 px-4 py-3 text-white text-center font-semibold shadow-sm cursor-pointer" onClick={() => setLocalFlash(f => ({ ...f, notice: null }))}>
            {localFlash.notice}
          </div>
        )}
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
